if (!customElements.get('catalog-manager-fitment')) {
  class CatalogManagerFitment extends HTMLElement {
    connectedCallback() {
      if (this.dataset.catalogManagerReady === 'true') return;

      this.dataset.catalogManagerReady = 'true';
      this.groups = [...this.querySelectorAll('[data-catalog-manager-group]')];
      this.groupStates = new Map();

      this.handleImmediateChange = this.handleImmediateChange.bind(this);
      this.addEventListener('change', this.handleImmediateChange, true);

      this.groups.forEach((group) => this.initializeGroup(group));
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleImmediateChange, true);

      this.groupStates.forEach((state) => state.activeRequest?.abort());
      this.groupStates.clear();
    }

    handleImmediateChange(event) {
      const select = event.target.closest('select[data-catalog-manager-filter-field]');
      if (!select || !this.contains(select)) return;

      const group = select.closest('[data-catalog-manager-group]');
      if (!group) return;

      const controls = this.getSelects(group);
      const changedIndex = controls.indexOf(select);

      if (changedIndex < 0) return;

      // Disable the rest of the chain before the asynchronous section response
      // arrives. This prevents a customer from opening stale options briefly.
      queueMicrotask(() => {
        controls.slice(changedIndex + 1).forEach((control) => {
          control.disabled = true;
        });
      });
    }

    initializeGroup(group) {
      const state = {
        activeRequest: null,
        renderSequence: 0
      };

      this.groupStates.set(group, state);

      const controls = this.getSelects(group);

      controls.forEach((select, index) => {
        select.addEventListener('change', () => {
          this.handleSelectChange(group, state, index);
        });
      });

      group.querySelector('[data-catalog-manager-submit]')?.addEventListener('click', () => {
        this.submitGroup(group);
      });

      this.syncGroupState(group, state);
      this.renderGroup(group, state, -1);
    }

    handleSelectChange(group, state, changedIndex) {
      const controls = this.getSelects(group);

      controls.slice(changedIndex + 1).forEach((control) => {
        control.value = '';
      });

      this.syncGroupState(group, state);
      this.renderGroup(group, state, changedIndex);
    }

    getSelects(group) {
      return [...group.querySelectorAll('select[data-catalog-manager-filter-field]')];
    }

    getFields(group) {
      return this.getSelects(group).map((select) => ({
        sourceType: select.dataset.sourceType || 'product_metafield',
        namespace: select.dataset.namespace || '',
        key: select.dataset.metafieldKey || ''
      }));
    }

    getFilterParam(field) {
      if (field.sourceType === 'standard_product_field') {
        const key = field.key === 'productType' ? 'product_type' : field.key;
        return `filter.p.${key}`;
      }

      return `filter.p.m.${field.namespace}.${field.key}`;
    }

    getCollectionHandle(group) {
      return group.dataset.collectionHandle || this.dataset.collectionHandle || 'all';
    }

    syncGroupState(group, state) {
      const controls = this.getSelects(group);
      const firstEmptyIndex = controls.findIndex((control) => !control.value);
      const pendingIndex = state.pendingIndex ?? -1;

      controls.forEach((control, index) => {
        const chainDisabled = index > (firstEmptyIndex < 0 ? controls.length - 1 : firstEmptyIndex);
        const requestDisabled = pendingIndex >= 0 && index > pendingIndex;

        control.disabled = chainDisabled || requestDisabled;
      });

      const submit = group.querySelector('[data-catalog-manager-submit]');
      if (submit) submit.disabled = false;
    }

    updateSubmitLabel(group, renderedGroup) {
      const button = group.querySelector('[data-catalog-manager-submit]');
      if (!button) return;

      const defaultLabel = button.dataset.defaultLabel || button.textContent.trim() || 'Search';
      const hasSelection = this.getSelects(group).some((select) => select.value);
      const resultCount = renderedGroup?.dataset.resultCount;

      const foundTemplate = button.dataset.foundLabel;
      const label = hasSelection && resultCount !== undefined && resultCount !== ''
        ? (foundTemplate ? foundTemplate.replace('__count__', resultCount) : `Found ${resultCount} items`)
        : defaultLabel;
      const labelElement = button.querySelector('.catalog-manager-submit-label');
      if (labelElement) labelElement.textContent = label;
      else button.textContent = label;
    }

    buildCollectionUrl(group) {
      const url = new URL(window.location.href);
      url.pathname = `/collections/${encodeURIComponent(this.getCollectionHandle(group))}`;
      url.hash = '';

      [...url.searchParams.keys()].forEach((key) => {
        if (key.startsWith('filter.') || key === 'section_id' || key === 'page') {
          url.searchParams.delete(key);
        }
      });

      if (group.dataset.productType) {
        url.searchParams.set('filter.p.product_type', group.dataset.productType);
      }

      const fields = this.getFields(group);
      this.getSelects(group).forEach((select, index) => {
        if (select.value) {
          url.searchParams.set(this.getFilterParam(fields[index]), select.value);
        }
      });

      return url;
    }

    async renderGroup(group, state, changedIndex) {
      const requestId = ++state.renderSequence;

      state.activeRequest?.abort();
      state.activeRequest = new AbortController();
      state.pendingIndex = changedIndex;
      this.syncGroupState(group, state);

      const url = this.buildCollectionUrl(group);
      url.searchParams.set('section_id', this.dataset.sectionId);

      try {
        const response = await fetch(url, {
          headers: { Accept: 'text/html' },
          signal: state.activeRequest.signal
        });
        const html = await response.text();

        if (requestId !== state.renderSequence) return;
        if (!response.ok) throw new Error(`Section request failed: ${response.status}`);

        const wrapper = document.createElement('div');
        wrapper.innerHTML = html;

        const nextGroup = wrapper.querySelector(
          `[data-catalog-manager-group="${CSS.escape(group.dataset.groupId)}"]`
        );
        const nextSelects = nextGroup
          ? [...nextGroup.querySelectorAll('select[data-catalog-manager-filter-field]')]
          : [];

        this.updateSubmitLabel(group, nextGroup);
        this.reconcileOptions(group, nextSelects, changedIndex);
      } catch {
        // An aborted request is expected when the customer changes a field again.
      } finally {
        if (requestId === state.renderSequence) {
          state.pendingIndex = -1;
          this.syncGroupState(group, state);
        }
      }
    }

    reconcileOptions(group, nextSelects, changedIndex) {
      const currentSelects = this.getSelects(group);

      currentSelects.forEach((select, index) => {
        if (index <= changedIndex || !nextSelects[index]) return;

        const availableOptions = new Map(
          [...nextSelects[index].options].map((option) => [option.value, option.disabled])
        );

        [...select.options].forEach((option) => {
          if (option.value) {
            option.disabled = availableOptions.has(option.value)
              ? availableOptions.get(option.value)
              : true;
          }
        });

        const selectedOption = select.options[select.selectedIndex];
        if (selectedOption?.disabled) {
          select.value = '';
          currentSelects.slice(index + 1).forEach((next) => {
            next.value = '';
          });
        }
      });

    }

    submitGroup(group) {
      window.location.href = this.buildCollectionUrl(group).toString();
    }
  }

  customElements.define('catalog-manager-fitment', CatalogManagerFitment);
}
